import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import StatusBadge from '../ui/StatusBadge';
import Button from '../ui/Button';
import LoadErrorBox from '../ui/LoadErrorBox';
import { FormInput, FormSelect } from '../FormField';
import ProcSection from './ProcSection';
import { useLoad } from '../../hooks/useLoad';
import { fetchShipments } from '../../services/ProcurementService';
import { entityPath } from '../../utils/entityRoutes';
import { PROGRESS_VARIANT } from './procurementLabels';
import type { OrderShipment } from '../../types/Procurement';
import { recordLabelOr } from '../../utils/recordId';
import { useRecordRefs } from '../../utils/recordRefs';

interface Props {
  shipments: OrderShipment[];
  canEdit: boolean;
  onAttach: (shipmentId: string, input: { cbm?: string | null; carton_count?: number | null }) => Promise<void>;
  onDetach: (shipment: OrderShipment) => Promise<void>;
}

/** Shipments an order travels on, with this order's share (CBM, cartons) of each. */
const OrderShipments: React.FC<Props> = ({ shipments, canEdit, onAttach, onDetach }) => {
  // Re-render when the record numbers load.
  useRecordRefs();
  const navigate = useNavigate();
  // The list to attach from is only needed by someone who can attach.
  const all = useLoad(() => (canEdit ? fetchShipments() : Promise.resolve([])), [canEdit]);
  const linked = useMemo(() => new Set(shipments.map(s => s.id)), [shipments]);
  const options = (all.data ?? [])
    .filter(s => !linked.has(s.id) && s.status !== 'cancelled')
    .map(s => ({ value: s.id, label: `${s.waybill_no || s.container_ref || s.id.slice(0, 8).toUpperCase()} (${s.progress_status})` }));
  const [pick, setPick] = useState('');
  const [cbm, setCbm] = useState('');
  const [cartons, setCartons] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const attach = async () => {
    if (!pick) { setError('Choose a shipment to link.'); return; }
    setSaving(true);
    setError(null);
    try {
      await onAttach(pick, { cbm: cbm.trim() || null, carton_count: cartons.trim() ? Number(cartons) : null });
      setPick(''); setCbm(''); setCartons('');
    } catch {
      setError('The shipment could not be linked. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <ProcSection title="Shipments" ariaLabel="Linked shipments">
      {shipments.length === 0 ? (
        <p className="text-subtle">This order is not on a shipment yet.</p>
      ) : (
        <div className="proc-scroll">
          <table className="proc-table">
            <thead><tr><th>Shipment</th><th>Progress</th><th>Waybill</th><th className="proc-num">CBM</th><th className="proc-num">Cartons</th>{canEdit && <th aria-label="Actions" />}</tr></thead>
            <tbody>
              {shipments.map(s => (
                <tr key={s.id}>
                  <td><button type="button" className="link-btn" onClick={() => navigate(entityPath('shipment', s.id) ?? '/procurement/shipments')}>{recordLabelOr('shipment', s.id, s.id.slice(0, 8).toUpperCase())}</button></td>
                  <td><StatusBadge variant={PROGRESS_VARIANT[s.progress_status] ?? 'neutral'}>{s.progress_status}</StatusBadge></td>
                  <td>{s.waybill_no ?? '—'}</td>
                  <td className="proc-num">{s.cbm ?? '—'}</td>
                  <td className="proc-num">{s.carton_count ?? '—'}</td>
                  {canEdit && <td className="proc-row-actions"><Button variant="ghost" size="sm" onClick={() => void onDetach(s)}>Unlink</Button></td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {canEdit && (
        all.error ? (
          <LoadErrorBox what="shipments" error={all.error} onRetry={all.reload} retrying={all.loading} />
        ) : (
          <>
            <div className="form-row">
              <FormSelect label="Link a shipment" value={pick} onChange={e => setPick(e.target.value)} options={[{ value: '', label: options.length ? 'Choose a shipment' : 'No shipment to link' }, ...options]} />
              <FormInput label="CBM of this order" inputMode="decimal" value={cbm} onChange={e => setCbm(e.target.value)} />
              <FormInput label="Cartons of this order" inputMode="numeric" value={cartons} onChange={e => setCartons(e.target.value)} />
            </div>
            <div className="proc-section-actions">
              <Button variant="secondary" size="sm" loading={saving} onClick={() => void attach()}>Link shipment</Button>
            </div>
            {error && <p role="alert" className="form-error-text">{error}</p>}
          </>
        )
      )}
    </ProcSection>
  );
};

export default OrderShipments;
